import Cookies from 'universal-cookie';
import axios from 'axios';

const getApiBaseUrl = () => {
    // Production — same-origin /api (Vercel rewrites to EC2). Avoids CORS + withCredentials issues.
    if (typeof window !== 'undefined') {
        const host = window.location.hostname;
        if (host === 'whattomake.co.za' || host === 'www.whattomake.co.za') {
            return '/api';
        }
    }
    if (process.env.REACT_APP_API_URL) {
        return process.env.REACT_APP_API_URL;
    }
    return '/api';
};

const API_BASE_URL = getApiBaseUrl();
// Create axios instance with default config
const axiosInstance = axios.create({
    baseURL: API_BASE_URL,
    timeout: 600000, // 10 minutes (600 seconds) for AI operations
    withCredentials: true,
    headers: {
        'Content-Type': 'application/json'
    }
});

// Override defaults for all requests
axios.defaults.withCredentials = true;

// Add a request interceptor to include auth token
axiosInstance.interceptors.request.use(
    config => {
        const cookies = new Cookies();
        // Try to get token from cookie first
        let token = cookies.get('access_token');
        
        // If not found in cookie, try localStorage as fallback
        if (!token) {
            token = localStorage.getItem('auth_token');
            console.log('No token in cookies, using localStorage token instead');
        }
        
        if (token) {
            config.headers['Authorization'] = `Bearer ${token}`;
            // Only log for non-polling requests to reduce noise
            if (!config.url.includes('/job-status/')) {
                console.log('Request with token:', {
                    url: config.url,
                    hasToken: !!token,
                    tokenLength: token.length,
                    source: cookies.get('access_token') ? 'cookie' : 'localStorage'
                });
            }
        } else {
            // Only log missing token for non-polling requests
            if (!config.url.includes('/job-status/')) {
                console.log('No token available for request to:', config.url);
            }
        }
        
        return config;
    },
    error => {
        console.error('Request interceptor error:', error);
        return Promise.reject(error);
    }
);

// Add retry logic
axiosInstance.interceptors.response.use(null, async error => {
    if (error.code === 'ECONNABORTED' || error.code === 'ERR_NETWORK') {
        const retries = error.config.retryCount || 0;
        if (retries < 3) {
            error.config.retryCount = retries + 1;
            return new Promise(resolve => {
                setTimeout(() => {
                    resolve(axiosInstance(error.config));
                }, 1000 * Math.pow(2, retries));
            });
        }
    }
    
    // If endpoint not found (404), just pass the error through
    // This is important for requests to endpoints that might not exist in production
    if (error.response && error.response.status === 404) {
        console.log(`Endpoint not found: ${error.config.url}`);
        return Promise.reject(error);
    }
    
    // Handle 401 errors (Unauthorized)
    if (error.response && error.response.status === 401) {
        console.error('Authentication error detected. Token may be invalid or expired.');
        
        // If it's a validate-token endpoint, don't clear credentials as it might be a transient error
        if (!error.config.url.includes('/auth/validate-token')) {
            // Clear cookies and localStorage
            const cookies = new Cookies();
            cookies.remove('access_token');
            localStorage.removeItem('auth_token');
            localStorage.removeItem('userID');
            
            console.log('Credentials cleared due to 401 error');
        } else {
            console.log('Not clearing credentials for validate-token endpoint 401');
        }
        
        // Don't redirect automatically as it might disrupt current operations
        // Instead, let the component handle the redirect
    }
    
    return Promise.reject(error);
});

// Add this for debugging
console.log('Using API URL:', API_BASE_URL);
console.log('Current NODE_ENV:', process.env.NODE_ENV);

const checkApiHealth = async () => {
    try {
        console.log('Checking API health...');
        const response = await fetch(`${API_BASE_URL}/health`, {
            timeout: 8000,
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
            }
        });
        console.log('Health check response:', response);
        return response.ok;
    } catch (error) {
        console.error('API health check failed:', error);
        return false;
    }
};

const fetchWithRetry = async (url, options = {}, retries = 3) => {
    for (let i = 0; i < retries; i++) {
        try {
            const cookies = new Cookies();
            // Try cookie first, then localStorage
            let token = cookies.get('access_token');
            
            if (!token) {
                token = localStorage.getItem('auth_token');
                console.log('Using token from localStorage instead of cookie');
            }
            
            if (!token) {
                console.log('No authentication token found in either cookie or localStorage');
                window.location.href = '/auth/login';
                return;
            }

            // Decode and check token expiration
            try {
                const payload = JSON.parse(atob(token.split('.')[1]));
                if (Date.now() >= payload.exp * 1000) {
                    console.log('Token expired');
                    cookies.remove('access_token');
                    localStorage.removeItem('auth_token');
                    localStorage.removeItem('userID');
                    window.location.href = '/auth/login';
                    return;
                }
            } catch (e) {
                console.error('Error parsing token:', e);
            }

            const response = await fetch(url, {
                ...options,
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                    ...options.headers
                }
            });

            if (response.status === 401) {
                cookies.remove('access_token');
                localStorage.removeItem('auth_token');
                localStorage.removeItem('userID');
                window.location.href = '/auth/login';
                return;
            }

            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.message || 'Request failed');
            }

            return await response.json();
        } catch (error) {
            if (i === retries - 1) throw error;
            await new Promise(resolve => setTimeout(resolve, 1000 * Math.pow(2, i)));
        }
    }
};

// Helper function to check if token is expired
const isTokenExpired = () => {
    const token = localStorage.getItem('token');
    if (!token) return true;
    
    try {
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const payload = JSON.parse(window.atob(base64));
        return payload.exp * 1000 < Date.now();
    } catch (error) {
        console.error('Error checking token expiration:', error);
        return true;
    }
};

export { API_BASE_URL, checkApiHealth, fetchWithRetry, isTokenExpired, axiosInstance };
